import ScenarioIcon from "@/Components/Icons/ScenarioIcon";
import { Box } from "@/Components/v2/atoms/Box";
import { OutlinedButton } from "@/Components/v2/atoms/Button/OutlinedButton";
import { PrimaryButton } from "@/Components/v2/atoms/Button/PrimaryButton";
import { Divider } from "@/Components/v2/atoms/Divider";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { FormatCurrency } from "@/hooks/useConfig/currency";
import { CheckCircleOutline } from "@mui/icons-material";
import { styled, useTheme } from "@mui/material";
import { ScenarioSummary } from "@mutiny-group/sbo-openapi-client";
import { isNil, upperFirst } from "lodash";
import { getStrategyProfile, StrategyStyle } from "../types";
import { RecommendationAction } from "./Recommendations";

const CardContainer = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.mode === "dark" ? "#656565" : "#e0e0e0"}`,
  borderRadius: "1rem",
  paddingTop: "1.5rem",
  paddingBottom: "1.5rem",
  paddingLeft: "1.5rem",
  paddingRight: "1.5rem",
  minWidth: "300px",
  maxWidth: "500px",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
}));

const CenteredContent = styled(Box)({
  maxWidth: "350px",
  margin: "0 auto",
  width: "100%",
});

const FullWidthDivider = styled(Divider)({
  marginLeft: "-1.5rem",
  marginRight: "-1.5rem",
});

const StrategyPill = styled(Box)<{ strategystyle: StrategyStyle }>(({ strategystyle }) => {
  return {
    ...strategystyle,
    color: "white",
    padding: "8px",
    borderRadius: "4px",
    fontSize: "16px",
    fontWeight: 600,
    textAlign: "center",
    width: "fit-content",
  };
});

const InvestmentList = styled(Box)({
  "& ul": {
    margin: 0,
    paddingLeft: "1.25rem",
  },
  "& li": {
    display: "list-item",
    listStyle: "disc outside",
    marginBottom: "0.25rem",
    fontSize: "14px",
  },
});

const MetricsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
});

const MetricRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const ButtonContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.75rem",
  marginTop: "auto",
});

type RecommendationCardProps = {
  recommendation: ScenarioSummary;
  handleAction: (action: RecommendationAction) => void;
  isApproveScenarioSending: boolean;
  isUnapproveScenarioSending: boolean;
  formatCurrency: FormatCurrency;
  isApprovedScenario: boolean;
};

export const RecommendationCard = ({
  recommendation,
  handleAction,
  formatCurrency,
  isApprovedScenario,
  isApproveScenarioSending,
  isUnapproveScenarioSending,
}: RecommendationCardProps) => {
  const { scenario, summary } = recommendation;
  const theme = useTheme();

  if (!recommendation || !scenario) return null;

  const investmentChanges = summary
    ? summary
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "")
        .map((s) => upperFirst(s))
    : [];

  const metrics = [
    {
      label: "Lower Bound",
      value: !isNil(scenario.revenueLowerBound)
        ? formatCurrency(scenario.revenueLowerBound)
        : "N/A",
    },
    {
      label: "Upper Bound",
      value: !isNil(scenario.revenueUpperBound)
        ? formatCurrency(scenario.revenueUpperBound)
        : "N/A",
    },
    {
      label: "Revenue Estimate",
      value: !isNil(scenario.revenue) ? formatCurrency(scenario.revenue) : "N/A",
    },
  ];

  const strategyProfile = getStrategyProfile(scenario.risk?.strategyProfile);
  if (!strategyProfile) return null;

  return (
    <CardContainer data-testid={`recommendation-card-${scenario.uuid}`}>
      <Stack direction="column" flex={1} gap={2}>
        <CenteredContent>
          <StrategyPill strategystyle={strategyProfile.style}>
            {strategyProfile.label} Strategy
          </StrategyPill>
        </CenteredContent>
        {investmentChanges.length > 0 && (
          <CenteredContent>
            <InvestmentList>
              <ul data-testid="investment-changes-list">
                {investmentChanges.map((part, index) => (
                  <li key={index} data-testid="investment-change-item">
                    {part}
                  </li>
                ))}
              </ul>
            </InvestmentList>
          </CenteredContent>
        )}
      </Stack>
      <FullWidthDivider />
      <CenteredContent>
        <MetricsContainer>
          {metrics.map((metric) => (
            <MetricRow key={metric.label}>
              <Typography fontSize={14}>{metric.label}:</Typography>
              <Typography fontSize={14} fontWeight={600}>
                {metric.value}
              </Typography>
            </MetricRow>
          ))}
        </MetricsContainer>
      </CenteredContent>
      <FullWidthDivider />
      <CenteredContent>
        <ButtonContainer>
          <PrimaryButton
            onClick={() => handleAction("view")}
            sx={{
              textAlign: "center",
              width: "100%",
            }}
          >
            View Scenario <ScenarioIcon sx={{ ml: "6px", fontSize: "1.2rem" }} />
          </PrimaryButton>
          {isApprovedScenario ? (
            <PrimaryButton
              data-testid="approved-scenario-button"
              sx={{
                textAlign: "center",
                width: "100%",
                backgroundColor: theme.palette.success.main,
                "&:hover": {
                  cursor: "default",
                  backgroundColor: theme.palette.success.main,
                },
              }}
            >
              <CheckCircleOutline sx={{ fontSize: "1.2rem", mr: "6px" }} />
              Approved
            </PrimaryButton>
          ) : (
            <OutlinedButton
              data-testid="approve-scenario-button"
              variant="outlined"
              onClick={() => handleAction("approve")}
              disabled={isApproveScenarioSending}
              sx={{
                textAlign: "center",
                width: "100%",
              }}
            >
              {isApproveScenarioSending ? "Approving..." : "Approve Scenario"}
            </OutlinedButton>
          )}
          {isApprovedScenario && (
            <OutlinedButton
              data-testid="unapprove-scenario-button"
              variant="outlined"
              onClick={() => handleAction("unapprove")}
              disabled={isUnapproveScenarioSending}
              sx={{
                textAlign: "center",
                width: "100%",
              }}
            >
              {isUnapproveScenarioSending ? "Unapproving..." : "Unapprove Scenario"}
            </OutlinedButton>
          )}
        </ButtonContainer>
      </CenteredContent>
    </CardContainer>
  );
};

export default RecommendationCard;
